import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ShieldCheckIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { activityAPI } from '../services/api';
import { formatDistanceToNow, format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { parseUTCDate } from '../utils/dateUtils';

const Security = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await activityAPI.getLogs();
      setLogs(response.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* App Bar */}
      <div className="sticky top-0 z-50 bg-white shadow-soft">
        <div className="flex items-center px-4 h-16">
          <button
            onClick={() => navigate('/profile')}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-slate-700" />
          </button>
          <div className="ml-2 flex flex-col justify-center">
            <h1 className="text-lg font-semibold text-slate-900 leading-tight">Keamanan Grup</h1>
            <span className="text-xs text-slate-500">Log Aktivitas</span>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4 pb-20">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-20 px-6">
            <div className="w-20 h-20 bg-primary/5 rounded-full flex items-center justify-center mb-6">
              <ShieldCheckIcon className="w-10 h-10 text-primary/50" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Riwayat Aman!</h3>
            <p className="text-slate-500 text-sm">
              Belum ada aktivitas yang direkam pada grup Anda.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {logs.map((log, idx) => (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
                >
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-surface flex items-center justify-center shrink-0">
                      <AdjustmentsHorizontalIcon className="w-6 h-6 text-slate-600" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="inline-flex px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-medium">
                          {log.group_name}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium shrink-0 pt-0.5">
                          {formatDistanceToNow(parseUTCDate(log.created_at), { addSuffix: true, locale: idLocale })}
                        </span>
                      </div>
                      
                      <h4 className="text-sm font-semibold text-slate-900 truncate mb-0.5">
                        {log.action}
                      </h4>
                      
                      {log.description && (
                        <p className="text-sm text-slate-600 leading-snug">
                          {log.description}
                        </p>
                      )}
                      
                      <div className="mt-2 text-[11px] text-slate-400 flex items-center gap-1.5">
                        <span className="font-medium text-slate-500">By: {log.user_name}</span>
                        <span>•</span>
                        <span>{format(parseUTCDate(log.created_at), 'd MMM yyyy, HH:mm', { locale: idLocale })}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default Security;
